import * as amplitude from '@amplitude/unified';
import { Identify } from '@amplitude/unified';

interface UtmParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
}

const UTM_SESSION_KEY = 'scribane_utms';
const UTM_KEYS: (keyof UtmParams)[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
];

const readUtmsFromUrl = (): UtmParams => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
    fbclid: params.get('fbclid'),
  };
};

/**
 * Persists UTM/click params from the current URL into sessionStorage as early
 * as possible — independent of cookie consent and before any redirect can drop
 * the query string. This matters for Instagram's in-app browser, where a fresh
 * webview means consent is re-prompted and the landing URL may be navigated
 * away before tracking initializes. Persisting is not sending: events are still
 * gated by consent in AmplitudeInit. Call once on app load.
 */
export const captureUtmsOnLoad = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const fromUrl = readUtmsFromUrl();
  if (UTM_KEYS.some((key) => fromUrl[key] !== null)) {
    sessionStorage.setItem(UTM_SESSION_KEY, JSON.stringify(fromUrl));
  }
};

const getUtms = (): UtmParams => {
  captureUtmsOnLoad();

  const stored = sessionStorage.getItem(UTM_SESSION_KEY);
  if (stored) {
    return JSON.parse(stored) as UtmParams;
  }

  return {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_content: null,
    utm_term: null,
    fbclid: null,
  };
};

/**
 * Sets first-touch campaign attribution as user properties so users can be
 * segmented by acquisition source. Stored once with `initial_*` (setOnce,
 * never overwritten). No-op when there are no UTM/click params present.
 * Last-touch user properties are intentionally not set; per-event UTM
 * properties already capture the campaign at the time of each event.
 */
export const identifyAttribution = (): void => {
  if (!window.__AMPLITUDE_INITIALIZED__) {
    return;
  }

  const utms = getUtms();
  const hasAttribution = UTM_KEYS.some((key) => utms[key] !== null);
  if (!hasAttribution) {
    return;
  }

  const identifyObj = new Identify();
  UTM_KEYS.forEach((key) => {
    const value = utms[key];
    if (value === null) {
      return;
    }
    identifyObj.setOnce(`initial_${key}`, value); // first touch
  });
  amplitude.identify(identifyObj);
};

const PENDING_EVENTS_KEY = 'scribane_pending_amplitude_events';

interface PendingEvent {
  name: string;
  properties: Record<string, unknown>;
}

const queueEvent = (name: string, properties: Record<string, unknown>): void => {
  const stored = sessionStorage.getItem(PENDING_EVENTS_KEY);
  const events: PendingEvent[] = stored ? (JSON.parse(stored) as PendingEvent[]) : [];
  events.push({ name, properties });
  sessionStorage.setItem(PENDING_EVENTS_KEY, JSON.stringify(events));
};

export const flushPendingEvents = (): void => {
  const stored = sessionStorage.getItem(PENDING_EVENTS_KEY);
  if (!stored) return;
  const events = JSON.parse(stored) as PendingEvent[];
  sessionStorage.removeItem(PENDING_EVENTS_KEY);
  events.forEach(({ name, properties }) => {
    amplitude.track(name, properties);
  });
};

const track = (eventName: string, properties: Record<string, unknown> = {}): void => {
  const eventProperties = { ...getUtms(), ...properties };
  if (!window.__AMPLITUDE_INITIALIZED__) {
    queueEvent(eventName, eventProperties);
    return;
  }
  amplitude.track(eventName, eventProperties);
};

export const identifyUser = (userId: string, email: string): void => {
  amplitude.setUserId(userId);
  const identifyObj = new Identify();
  identifyObj.set('email', email);
  amplitude.identify(identifyObj);
};

export const trackPageViewed = (page: string, userId?: string): void => {
  track('page_viewed', { page, user_id: userId ?? null });
};

export const trackCtaClicked = (ctaLabel: string, section: string): void => {
  track('cta_clicked', { cta_label: ctaLabel, section });
};

export const trackRegisterStarted = (invitationCode: string | undefined, locale: string): void => {
  track('register_started', { invitation_code: invitationCode ?? null, locale });
};

export const trackRegisterCompleted = (invitationCode: string | undefined, locale: string): void => {
  track('register_completed', { invitation_code: invitationCode ?? null, locale });
};

export const trackFirstProjectCreated = (userId: string): void => {
  track('first_project_created', { user_id: userId });
};

export const trackMenuClicked = (option: string): void => {
  track('menu_clicked', { option });
};

export const trackCookieConsentUpdated = (action: 'accept_all' | 'reject_all' | 'save_preferences', analytics: boolean): void => {
  track('cookie_consent_updated', { action, analytics });
};

export const trackProjectCreationSuccess = (): void => {
  track('project_creation_success');
};

export const trackProjectCreationFailed = (error: 'project_limit_reached' | 'unknown'): void => {
  track('project_creation_failed', { error });
};

export const trackProjectImportStarted = (): void => {
  track('project_import_started');
};

export const trackProjectImportSuccess = (): void => {
  track('project_import_success');
};

export const trackProjectImportFailed = (error: 'empty_file' | 'content_too_large' | 'project_limit_reached' | 'unknown'): void => {
  track('project_import_failed', { error });
};

export const trackSuggestionAccepted = (issueType: string): void => {
  track('suggestion_accepted', { issue_type: issueType });
};

interface ImportAnalysisOptions {
  suggestions: boolean;
  characters: boolean;
  places: boolean;
}

export const trackProjectImportAnalysisStarted = (options: ImportAnalysisOptions): void => {
  track('project_import_analysis_started', { options });
};

export const trackProjectImportAnalysisSuccess = (options: ImportAnalysisOptions): void => {
  track('project_import_analysis_success', { options });
};

export const trackProjectImportAnalysisFailed = (options: ImportAnalysisOptions): void => {
  track('project_import_analysis_failed', { options });
};
